import React, { useState, useEffect, useMemo, useCallback } from "react";
import { usePageApiQueue } from "../../Components/Hooks/PageApiQueue";
import {
  Card,
  CardBody,
  CardHeader,
  Modal,
  ModalHeader,
  ModalBody
} from "reactstrap";
import ReactApexChart from "react-apexcharts";
import Loader from "../../Components/Common/Loader";
import { postDataAPI } from "../../helpers/phpbackend_helper";
import { POST_MENU_OPTIMIZATION } from "../../helpers/data_helper";
import {
  getDateByName,
  convertDateFormate,
  currencyFormatWithAlert,
} from "../../helpers/api_helper";

const CourseProductClassification = ({ 
  leftFilter, 
  rightFilter, 
  leftCustomDate, 
  mealPeriod, 
  currency, 
  defaultCourse, 
  comparisonDate, 
  activeComparisonData, 
  eventFilter,
  chartMax = 100 
}) => {
  const { enqueue } = usePageApiQueue();
  const [chartData, setChartData] = useState({
    categories: [],
    series: {
      Star: [],
      Puzzle: [],
      "Cash Cow": [],
      Dog: []
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [modalAjax, setModalAjax] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const getEmptyChartData = useCallback(() => ({
    categories: [],
    courseIds: [],
    courseMap: {},
    series: {
      Star: [],
      Puzzle: [],
      "Cash Cow": [],
      Dog: [],
    },
  }), []);

  const mapCourseResponse = useCallback((responseData) => {
    const courses = Array.isArray(responseData)
      ? responseData.map((course, index) => [course.id || index, course])
      : Object.entries(responseData || {});
    const mappedData = getEmptyChartData();

    courses.forEach(([courseId, course]) => {
      if (!course || typeof course !== "object") return;

      mappedData.courseIds.push(String(courseId));
      mappedData.categories.push(course.cfv_display_name || String(courseId));
      mappedData.courseMap[String(courseId)] = course.cfv_display_name || String(courseId);

      const groups = course.performance_groups || [];
      const groupValues = {};
      groups.forEach((group) => {
        groupValues[group.performance_group] = Number(group.item_sold) || 0;
      });

      mappedData.series.Star.push(groupValues.Star || 0);
      mappedData.series.Puzzle.push(groupValues.Puzzle || 0);
      mappedData.series["Cash Cow"].push(groupValues["Cash Cow"] || 0);
      mappedData.series.Dog.push(groupValues.Dog || 0);
    });

    return mappedData;
  }, [getEmptyChartData]);

  // Map course names to IDs for modal click
  const courseNameToIdMap = useMemo(() => {
    const m = {};
    if (chartData.courseMap) {
      Object.entries(chartData.courseMap).forEach(([id, name]) => {
        m[name] = id;
      });
    }
    return m;
  }, [chartData.courseMap]);

  // Prepare series data for ApexCharts - ensure all series have data
  const series = [
    {
      name: "Dog",
      data: chartData.series.Dog || [],
    },
    {
      name: "Cash Cow",
      data: chartData.series["Cash Cow"] || [],
    },
    {
      name: "Puzzle",
      data: chartData.series.Puzzle || [],
    },
    {
      name: "Star",
      data: chartData.series.Star || [],
    },
  ];

  const stackedTotals = chartData.categories.map((_, index) =>
    series.reduce((total, item) => total + (Number(item.data[index]) || 0), 0)
  );
  const maxStackedTotal = Math.max(...stackedTotals, chartMax, 100);
  const yAxisMax = Math.ceil((maxStackedTotal * 1.1) / 100) * 100;

  const options = {
    chart: {
      type: "bar",
      stacked: true,
      stackType: "normal",
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
      events: {
        dataPointSelection: function (event, chartContext, config) {
          const { dataPointIndex } = config;
          if (dataPointIndex === -1) return;

          const courseName = chartData.categories[dataPointIndex];
          const courseId = courseNameToIdMap[courseName] || chartData.courseIds?.[dataPointIndex];

          if (courseId) {
            setTimeout(() => handleDataPointClick(courseId), 0);
          }
        },
        click: function (event, chartContext, config) {
          try {
            const target = event.target;
            if (target && (target.tagName === 'text' || target.tagName === 'tspan')) {
              const text = target.textContent?.trim();
              if (text && chartData.categories.includes(text)) {
                const dataPointIndex = chartData.categories.indexOf(text);
                const courseId = courseNameToIdMap[text] || chartData.courseIds?.[dataPointIndex];
                if (courseId) {
                  handleDataPointClick(courseId);
                }
              }
            }
          } catch (err) {
            console.error(err);
          }
        },
      }
    },

    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        borderRadius: 0,
        borderRadiusApplication: "around",
        borderRadiusWhenStacked: "all",
      },
    },

    dataLabels: {
      enabled: false,
    },

    xaxis: {
      categories: chartData.categories || [],
      labels: {
        rotate: -45,
        rotateAlways: false,
        trim: true,
        hideOverlappingLabels: false,
        style: {
          colors: "#6c757d",
          fontSize: "12px",
          fontWeight: 500,
          fontFamily: "Poppins, sans-serif",
        },
      },
      axisBorder: {
        show: true,
        color: "#e9e9ef",
      },
      axisTicks: {
        show: true,
        color: "#e9e9ef",
      },
    },

    yaxis: {
      min: 0,
      max: yAxisMax,
      tickAmount: 5,
      labels: {
        style: {
          colors: "#6c757d",
          fontSize: "12px",
          fontWeight: 500,
          fontFamily: "Poppins, sans-serif",
        },
        formatter: function (value) {
          return Math.round(value);
        },
      },
      title: {
        text: "Items Sold",
        style: {
          fontSize: "13px",
          fontWeight: 600,
          color: "#495057",
          fontFamily: "Poppins, sans-serif",
        },
      },
    },

    tooltip: {
      shared: true,
      intersect: false,
      theme: "light",
      style: {
        fontSize: "12px",
        fontFamily: "Poppins, sans-serif",
      },
      y: {
        formatter: (value) => {
          return `${value}`;
        },
      },
    },

    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      floating: false,
      fontSize: "13px",
      fontFamily: "Poppins, sans-serif",
      fontWeight: 500,
      labels: {
        colors: "#495057",
        useSeriesColors: false,
      },
      markers: {
        width: 14,
        height: 14,
        radius: 2,
        offsetX: 0,
        offsetY: 0,
      },
      itemMargin: {
        horizontal: 15,
        vertical: 5,
      },
    },

    // Updated colors with better contrast - keeping original but adding borders
    colors: [
      "#F06548",  // Dog - Light Red
      "#F7B84B",  // Cash Cow - Light Yellow
      "#F3F6F9",  // Puzzle - Light Green
      "#0AB39C",  // Star - Light Gray/White
    ],

    fill: {
      opacity: 1,
      type: "solid",
    },

    stroke: {
      show: true,
      width: 1,
      colors: ["rgba(0,0,0,0.1)"],
    },

    grid: {
      borderColor: "#e9e9ef",
      strokeDashArray: 4,
      position: "back",
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },

    states: {
      hover: {
        filter: {
          type: "darken",
          value: 0.1,
        },
      },
    },

    responsive: [
      {
        breakpoint: 768,
        options: {
          plotOptions: {
            bar: {
              columnWidth: "70%",
            },
          },
          xaxis: {
            labels: {
              rotate: -60,
              fontSize: "10px",
            },
          },
          legend: {
            position: "bottom",
            fontSize: "11px",
          },
        },
      },
      {
        breakpoint: 576,
        options: {
          plotOptions: {
            bar: {
              columnWidth: "80%",
            },
          },
          xaxis: {
            labels: {
              rotate: -90,
              fontSize: "9px",
            },
          },
        },
      },
    ],
  };

  const handleDataPointClick = (courseId) => {
    setSelectedCourse(courseId);
    
    let leftFilterData =
      leftFilter.value === "Custom"
        ? {
            from: convertDateFormate(leftCustomDate[0]),
            to: convertDateFormate(leftCustomDate[1]),
          }
        : getDateByName(leftFilter.value);

    setModal(true);
    setModalAjax(true);

    postDataAPI({
      course: courseId,
      dateFilter: leftFilterData,
      event_filter: eventFilter,
      serviceName: "menu_data",
      op: "get_items_under_tribe",
    }).then((response) => {
      setModalAjax(false);
      if (response.status === "ok") {
        setModalData(response?.data || []);
      } else {
        console.log(response);
      }
    });
  };

  // Fetch data from API
  useEffect(() => {
    if (
      (leftFilter.value !== "Custom" ||
        (leftFilter.value === "Custom" &&
          leftCustomDate[0] &&
          leftCustomDate[1])) &&
      (rightFilter.value !== "Custom" ||
        (rightFilter.value === "Custom" &&
          comparisonDate[0] &&
          comparisonDate[1]))
    ) {
      let leftFilterData =
        leftFilter.value === "Custom"
          ? {
              from: convertDateFormate(leftCustomDate[0]),
              to: convertDateFormate(leftCustomDate[1]),
            }
          : getDateByName(leftFilter.value);
      
      let rightFilterData =
        activeComparisonData && activeComparisonData.datepiker
          ? {
              from: convertDateFormate(comparisonDate[0]),
              to: convertDateFormate(comparisonDate[1]),
            }
          : getDateByName(rightFilter.value);

      setIsLoading(true);

      return enqueue(
        "CourseProductClassification",
        2,
        async (signal) => {
          try {
            const response = await postDataAPI({
              leftFilter: leftFilterData,
              rightFilter: rightFilterData,
              mealPeriod: mealPeriod,
              course: defaultCourse,
              event_filter: eventFilter,
              serviceName: POST_MENU_OPTIMIZATION.serviceName,
              op: "course_wise_sold_performance_group",
            }, signal);

            if (signal.aborted) return;

            setIsLoading(false);

            if (response.status === "ok") {
              const data = response.data;
              console.log("CourseProductClassification API Response:", data);
              
              setChartData(mapCourseResponse(data));
            } else {
              console.log(response);
              setChartData(getEmptyChartData());
            }
          } catch (e) {
            setIsLoading(false);
          }
        }
      );
    }
  }, [
    leftFilter,
    rightFilter,
    mealPeriod,
    comparisonDate,
    activeComparisonData,
    leftCustomDate,
    defaultCourse,
    eventFilter,
    enqueue,
    mapCourseResponse,
    getEmptyChartData,
  ]);

  // Log data for debugging
  console.log("Chart Data:", chartData);
  console.log("Series Data:", series);

  return (
    <>
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="d-flex align-items-start">
            <div className={`icon-box rounded bg-danger text-white`}>
              <i className="ri-bar-chart-fill"></i>
            </div>
            <div className="ps-2">
              <h4 className="card-title mb-1">
                Courses Analysis
              </h4>
              <p className="text-muted mb-0">
                Item classification by course.
              </p>
            </div>
          </div>        
        </CardHeader>
        <CardBody className="px-3 pb-3 pt-2">
          {isLoading && (
            <div style={{ minHeight: "350px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Loader />
            </div>
          )}
          <div 
            id="CourseProductClassification" 
            className={isLoading ? "opacity-0" : ""}
            style={{ minHeight: "350px" }}
          >
            <ReactApexChart
              options={options}
              series={series}
              type="bar"
              height={350}
            />
          </div>
        </CardBody>
      </Card>

      {/* Modal for item details */}
      <Modal
        className="modal-dialog modal-xl custom-bg"
        isOpen={modal}
        toggle={() => setModal(false)}
        id="fullscreeexampleModal"
        size="xl"
      >
        <ModalHeader className="modal-title" id="exampleModalFullscreenLabel" toggle={() => setModal(false)}>
          {modalData[0]?.cfv_display_name || "Course Details"}
        </ModalHeader>
        <ModalBody>
          <Card style={{ borderRadius: '8px', padding: '15px' }}>
            <CardBody>
              {modalAjax && <Loader />}
              {!modalAjax && modalData?.length > 0 && (
                <div className="table-responsive table-card" style={{ maxHeight: '500px', overflow: 'auto' }}>
                  <table className="table table-borderless table-centered align-middle table-nowrap mb-3 text-center">
                    <thead className="text-muted table-light">
                      <tr style={{ position: 'sticky', top: -2, zIndex: 100 }}>
                        <th scope="col" className="text-start">Item Name</th>
                        <th scope="col">Performance Group</th>
                        <th scope="col">Item Sold</th>
                        <th scope="col">Avg Check</th>
                        <th scope="col">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalData?.map((item, index) => (
                        <tr key={index}>
                          <td className="text-start">
                            <b>{item.item_name}</b>
                          </td>
                          <td>
                            <span
                              className={`badge bg-${
                                item.performance_group === "Plowhorse"
                                  ? "warning"
                                  : item.performance_group === "Dog"
                                    ? "danger"
                                    : item.performance_group === "Cash Cow"
                                      ? "warning"
                                      : item.performance_group === "Puzzle"
                                        ? "light text-dark"
                                        : "success"
                              }`}
                            >
                              {item.performance_group}
                            </span>
                          </td>
                          <td>{item.item_sold}</td>
                          <td>{currencyFormatWithAlert((item.total_sales / item.item_sold), currency)}</td>
                          <td>{currencyFormatWithAlert(item.total_sales, currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!modalAjax && modalData?.length === 0 && (
                <p className="text-center text-muted py-4">No data available</p>
              )}
            </CardBody>
          </Card>
        </ModalBody>
      </Modal>
    </>
  );
};

export default CourseProductClassification;